import { buildAppleLoginRequestBody } from '../login/appleSignIn';
import { buildGoogleLoginRequestBody } from '../login/googleSignIn';
import type { LoginOption } from '../login/loginConstants';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import {
  assetCreateShowsRegionField,
  resolveIsraelDefaultRegionId,
} from './assetCreateFormDefaults';

/** Shown in UI only — omitted from POST (backend default is empty string). */
export const BUSINESS_CLIENT_HIDDEN_REGISTER_KEYS = ['business_type'] as const;

export const BUSINESS_CLIENT_REGISTER_BACKEND_DEFAULTS: Record<string, unknown> = {
  business_type: '',
};

/** Exact root keys for POST /register/BusinessClient (plus Google auth fields when applicable). */
export const BUSINESS_CLIENT_REGISTER_POST_ROOT_KEYS = [
  'email',
  'password',
  'phone_number',
  'country_id',
  'state_id',
  'business_id',
  'business_name',
  'business_type',
  'credits_per_employee',
  'first_name',
  'last_name',
] as const;

/** Google auth fields allowed on POST /register/BusinessClient when signing up with Google. */
export const BUSINESS_CLIENT_REGISTER_GOOGLE_SUPPLEMENT_KEYS = [
  'credential',
  'id_token',
  'google_id_token',
  'provider',
  'login_type',
  'email',
  'identifier',
] as const;

export const BUSINESS_CLIENT_REGISTER_APPLE_SUPPLEMENT_KEYS = [
  'credential',
  'id_token',
  'apple_id_token',
  'provider',
  'login_type',
  'email',
  'identifier',
  'first_name',
  'last_name',
  'full_name',
  'user',
] as const;

const BUSINESS_CLIENT_STRIP_FROM_FORM_KEYS = [
  'is_business',
  'phone_number_2',
  'payout_country',
  'account_holder_name',
  'bank_number',
  'branch_number',
  'account_number',
  'routing_number',
  'account_type',
  'payout_transfer_day',
  'bank_account',
  'address',
] as const;

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

export function isBusinessClientRegisterFlow(selectedOption: string): boolean {
  return selectedOption === 'business client';
}

function isEmptyRegisterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return false;
}

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Business-client register shows district/state only for the United States. */
export function businessClientRegisterShowsRegionField(countryId: unknown): boolean {
  return assetCreateShowsRegionField(countryId);
}

/** Wire defaults applied on register submit when fields are omitted from the UI. */
export function applyBusinessClientRegisterSubmitDefaults(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[] = [],
): Record<string, unknown> {
  const next = { ...body };
  if (!businessClientRegisterShowsRegionField(next.country_id)) {
    const currentRegion = coerceNumericId(next.state_id);
    if (currentRegion === null) {
      const defaultRegionId = resolveIsraelDefaultRegionId(regions);
      if (defaultRegionId !== null) {
        next.state_id = defaultRegionId;
      }
    }
  }
  return next;
}

const BUSINESS_CLIENT_REGISTER_MANDATORY_KEYS_BASE = [
  'business_name',
  'business_id',
  'contact_first_name',
  'contact_last_name',
  'email',
  'phone_number',
  'password',
  'confirm_password',
  'credits_per_employee',
  'country_id',
] as const;

export function isValidCreditsPerEmployee(value: unknown): boolean {
  return normalizeCreditsPerEmployee(value) !== null;
}

export function normalizeCreditsPerEmployee(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return Math.max(0, Math.trunc(parsed));
  }
  return null;
}

/** Business-client register fields shown in DynamicRecordForm. */
export function businessClientRegisterVisibleData(
  payload: Record<string, unknown>,
  options?: { omitPasswordFields?: boolean },
): Record<string, unknown> {
  const out = { ...payload };
  for (const key of BUSINESS_CLIENT_HIDDEN_REGISTER_KEYS) {
    delete out[key];
  }
  for (const key of BUSINESS_CLIENT_STRIP_FROM_FORM_KEYS) {
    delete out[key];
  }
  delete out.logo;
  delete out.logo_url;
  if (!businessClientRegisterShowsRegionField(payload.country_id)) {
    delete out.state_id;
  }
  if (options?.omitPasswordFields) {
    delete out.password;
    delete out.confirm_password;
  }
  return out;
}

export function businessClientRegisterMandatoryKeys(countryId?: unknown): readonly string[] {
  if (businessClientRegisterShowsRegionField(countryId)) {
    return [...BUSINESS_CLIENT_REGISTER_MANDATORY_KEYS_BASE, 'state_id'];
  }
  return [...BUSINESS_CLIENT_REGISTER_MANDATORY_KEYS_BASE];
}

/** First missing required root field key, or null when the form can submit. */
export function missingBusinessClientRegisterRequiredField(
  payload: Record<string, unknown>,
  options?: { skipPassword?: boolean },
): string | null {
  for (const key of businessClientRegisterMandatoryKeys(payload.country_id)) {
    if (
      options?.skipPassword &&
      (key === 'password' || key === 'confirm_password')
    ) {
      continue;
    }
    if (key === 'credits_per_employee') {
      if (!isValidCreditsPerEmployee(payload[key])) {
        return key;
      }
      continue;
    }
    if (isEmptyRegisterValue(payload[key])) {
      return key;
    }
  }
  return null;
}

function withBusinessClientRegisterBackendDefaults(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...payload };
  for (const key of BUSINESS_CLIENT_HIDDEN_REGISTER_KEYS) {
    const current = out[key];
    if (current === undefined || current === null || current === '') {
      out[key] = BUSINESS_CLIENT_REGISTER_BACKEND_DEFAULTS[key];
    }
  }
  return out;
}

function readRegisterId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }
  return null;
}

function pickBusinessClientRegisterPostRoot(out: Record<string, unknown>): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const key of BUSINESS_CLIENT_REGISTER_POST_ROOT_KEYS) {
    const value = out[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === 'string' && !value.trim() && key !== 'business_type') {
      continue;
    }
    picked[key] = value;
  }
  return picked;
}

/** POST /register/BusinessClient — whitelist matches backend contract. */
export function prepareBusinessClientRegisterPostBody(
  payload: Record<string, unknown>,
  googleCredential?: string | null,
  loginType: LoginOption = 'business client',
  regions: readonly GeneralInfoPicklistItem[] = [],
  appleCredential?: string | null,
): Record<string, unknown> {
  const out = withBusinessClientRegisterBackendDefaults(
    applyBusinessClientRegisterSubmitDefaults(payload, regions),
  );
  delete out.confirm_password;
  delete out.registration_date;
  delete out.address;

  out.business_type = BUSINESS_CLIENT_REGISTER_BACKEND_DEFAULTS.business_type;

  const contactFirst =
    pickString(out.contact_first_name) || pickString(out.first_name);
  const contactLast =
    pickString(out.contact_last_name) || pickString(out.last_name);
  delete out.contact_first_name;
  delete out.contact_last_name;
  if (contactFirst) {
    out.first_name = contactFirst;
  } else {
    delete out.first_name;
  }
  if (contactLast) {
    out.last_name = contactLast;
  } else {
    delete out.last_name;
  }

  const credits = normalizeCreditsPerEmployee(out.credits_per_employee);
  if (credits !== null) {
    out.credits_per_employee = credits;
  } else {
    delete out.credits_per_employee;
  }

  delete out.logo;
  delete out.logo_url;

  for (const key of BUSINESS_CLIENT_STRIP_FROM_FORM_KEYS) {
    delete out[key];
  }

  const countryId = readRegisterId(out.country_id);
  if (countryId !== null) {
    out.country_id = countryId;
  } else {
    delete out.country_id;
  }

  const stateId = readRegisterId(out.state_id);
  if (stateId !== null) {
    out.state_id = stateId;
  } else {
    delete out.state_id;
  }

  const picked = pickBusinessClientRegisterPostRoot(out);

  if (appleCredential?.trim()) {
    const appleBody = buildAppleLoginRequestBody(appleCredential, loginType);
    delete picked.password;
    const supplement: Record<string, unknown> = {};
    for (const key of BUSINESS_CLIENT_REGISTER_APPLE_SUPPLEMENT_KEYS) {
      const value = appleBody[key];
      if (value !== undefined && value !== null) {
        supplement[key] = value;
      }
    }
    return { ...picked, ...supplement };
  }

  if (googleCredential?.trim()) {
    const googleBody = buildGoogleLoginRequestBody(googleCredential, loginType);
    delete picked.password;
    const supplement: Record<string, unknown> = {};
    for (const key of BUSINESS_CLIENT_REGISTER_GOOGLE_SUPPLEMENT_KEYS) {
      const value = googleBody[key];
      if (value !== undefined && value !== null) {
        supplement[key] = value;
      }
    }
    return { ...picked, ...supplement };
  }

  return picked;
}
